import { useState, useEffect } from "react";
import { Target, Zap, CheckCircle2, XCircle, Clock, TrendingUp, AlertCircle, RefreshCw, BarChart3, Calendar } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  PENDING: { bg: "bg-slate-800/60", border: "border-slate-700", text: "text-slate-300", icon: Clock, label: "En attente" },
  ENTERED: { bg: "bg-cyan-500/10", border: "border-cyan-500/40", text: "text-cyan-300", icon: TrendingUp, label: "Position ouverte" },
  TRAILING: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300", icon: Zap, label: "Trail actif" },
  EXITED: { bg: "bg-emerald-500/20", border: "border-emerald-500/50", text: "text-emerald-300", icon: CheckCircle2, label: "Target atteint" },
  STOPPED: { bg: "bg-red-500/15", border: "border-red-500/40", text: "text-red-300", icon: XCircle, label: "Stop touché" },
  EXPIRED: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-300", icon: AlertCircle, label: "Expiré (EOD)" },
  PRICE_UNAVAILABLE: { bg: "bg-slate-800/40", border: "border-slate-700", text: "text-slate-400", icon: Clock, label: "Prix indispo" },
};

const CONFIDENCE_COLOR = {
  "A+": "text-emerald-300 bg-emerald-500/20 border-emerald-500/50",
  "A": "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
  "B+": "text-cyan-400 bg-cyan-500/15 border-cyan-500/30",
  "B": "text-amber-400 bg-amber-500/10 border-amber-500/30",
};

function PlayCard({ play, status, onAnalyze }) {
  const style = STATUS_STYLES[status?.status] || STATUS_STYLES.PENDING;
  const Icon = style.icon;
  const live = status?.live_price;
  const pnl = status?.pnl_pct;
  const progress = status?.progress_to_target_pct || 0;

  // Currency symbol — defaults to $ for USD
  const currSymbol = {
    "USD": "$", "EUR": "€", "GBP": "£", "CHF": "CHF "
  }[play._currency || "USD"] || "$";
  const region = play._region || "US";

  const fmtPrice = (v) => v == null ? "—" : `${currSymbol}${Number(v).toFixed(2)}`;

  return (
    <div className={`rounded-xl p-4 border ${style.bg} ${style.border} transition-all hover:scale-[1.01]`}
      data-testid={`morning-play-rank-${play.rank}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">Rank</span>
          <span className="text-2xl font-black text-white leading-none">#{play.rank}</span>
          <button onClick={() => onAnalyze && onAnalyze(play.ticker)}
            className="ml-2 font-mono text-lg font-bold text-cyan-300 hover:text-cyan-200 hover:underline"
            data-testid={`play-ticker-${play.rank}`}>{play.ticker}</button>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${CONFIDENCE_COLOR[play.confidence] || "text-slate-400 border-slate-600"}`}>
            {play.confidence}
          </span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${region === "EU" ? "text-blue-300 bg-blue-500/15 border-blue-500/40" : "text-amber-200 bg-amber-500/10 border-amber-500/30"}`}
            data-testid={`play-region-${play.rank}`}>
            {region === "EU" ? "🇪🇺 EU" : "🇺🇸 US"}
          </span>
        </div>
        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${style.text} ${style.border}`}>
          <Icon className="w-3 h-3" />
          {style.label}
        </span>
      </div>

      {/* Setup type + Thesis */}
      <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">{play.setup_type?.replace(/_/g, " ")}</div>
      <p className="text-sm text-slate-200 mb-3 leading-snug">{play.thesis}</p>

      {/* Plan grid */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="rounded p-2 bg-slate-900/50 border border-slate-700/50">
          <div className="text-[9px] text-slate-500 uppercase">Entry</div>
          <div className="font-mono text-sm font-bold text-white">{fmtPrice(play.entry)}</div>
        </div>
        <div className="rounded p-2 bg-red-900/20 border border-red-500/30">
          <div className="text-[9px] text-red-400 uppercase">Stop</div>
          <div className="font-mono text-sm font-bold text-red-300">{fmtPrice(play.stop)}</div>
        </div>
        <div className="rounded p-2 bg-emerald-900/20 border border-emerald-500/30">
          <div className="text-[9px] text-emerald-400 uppercase">Target</div>
          <div className="font-mono text-sm font-bold text-emerald-300">{fmtPrice(play.target)}</div>
        </div>
        <div className="rounded p-2 bg-cyan-900/20 border border-cyan-500/30">
          <div className="text-[9px] text-cyan-400 uppercase">R:R / Taille</div>
          <div className="font-mono text-sm font-bold text-cyan-300">{play.rr}:1 · {play.size_pct}%</div>
        </div>
      </div>

      {/* Live tracking */}
      {live != null && (
        <div className="rounded p-2 bg-slate-900/40 border border-slate-700/40 mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500 uppercase">Live</span>
              <span className="font-mono text-sm font-bold text-white">{fmtPrice(live)}</span>
              {pnl != null && (
                <span className={`font-mono text-xs font-bold ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}%
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-500">{progress.toFixed(0)}% du target</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${progress >= 80 ? "bg-emerald-400" : progress >= 50 ? "bg-cyan-400" : "bg-slate-500"} transition-all`}
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
          </div>
          {status?.next_action && (
            <div className="text-[11px] text-slate-300 mt-2 italic">→ {status.next_action}</div>
          )}
        </div>
      )}

      {/* Catalyst */}
      <div className="mb-3 p-2 rounded bg-slate-900/30 border border-slate-700/30">
        <div className="text-[9px] text-amber-400 uppercase tracking-wider mb-0.5">Catalyseur</div>
        <div className="text-xs text-slate-300">{play.catalyst}</div>
      </div>

      {/* Framework */}
      {play.framework && play.framework.length > 0 && (
        <details className="text-xs text-slate-400" data-testid={`play-framework-${play.rank}`}>
          <summary className="cursor-pointer text-cyan-400 font-semibold hover:text-cyan-300">📋 Framework étape par étape</summary>
          <ol className="list-decimal list-inside mt-2 space-y-1 ml-1">
            {play.framework.map((step, i) => <li key={i} className="text-slate-300">{step}</li>)}
          </ol>
          {play.risk_warning && (
            <div className="mt-2 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
              ⚠️ {play.risk_warning}
            </div>
          )}
        </details>
      )}
    </div>
  );
}

export default function MorningPocketPlays({ owner, onAnalyze }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const fetchPlays = async () => {
    if (!owner) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/morning-plays/${owner}`, { timeout: 90000 });
      setData(res.data);
    } catch (e) {
      console.error("morning plays err", e);
      setData({ plays: [], error: "Erreur de génération" });
    } finally { setLoading(false); }
  };

  const fetchStatuses = async () => {
    if (!data?.plays || data.plays.length === 0) return;
    setRefreshing(true);
    const next = {};
    await Promise.all(data.plays.map(async (p) => {
      try {
        const r = await axios.get(`${API}/morning-plays/${owner}/track/${p.rank}`, { timeout: 10000 });
        next[p.rank] = r.data;
      } catch (e) { /* ignore */ }
    }));
    setStatuses(next);
    setRefreshing(false);
  };

  useEffect(() => { fetchPlays(); }, [owner]);
  useEffect(() => {
    if (data?.plays?.length) {
      fetchStatuses();
      const id = setInterval(fetchStatuses, 90000); // 90s refresh
      return () => clearInterval(id);
    }
  }, [data?.plays?.length]);

  const handleRegen = async () => {
    if (!window.confirm("Forcer la régénération des 3 plays ? (ne fais ça qu'en cas de bug — normalement les plays sont stables pour la journée)")) return;
    setLoading(true);
    try {
      await axios.post(`${API}/morning-plays/${owner}/regenerate`);
      await fetchPlays();
    } finally { setLoading(false); }
  };

  if (!owner) return null;

  return (
    <div className="glass-card rounded-xl p-4 sm:p-5 mb-4 sm:mb-6" data-testid="morning-pocket-plays">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-cyan-400" />
          <div>
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Morning Pocket Plays</h3>
            <p className="text-[10px] text-slate-500">3 setups pré-planifiés · entry/stop/target avant l'open · suivi temps réel</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.session_date && (
            <span className="text-[10px] text-slate-500 font-mono">{data.session_date}</span>
          )}
          <button onClick={fetchStatuses} disabled={refreshing}
            className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-all"
            title="Rafraîchir prix live" data-testid="morning-plays-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
          <button onClick={handleRegen}
            className="px-2 py-1 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20"
            title="Régénérer (admin)" data-testid="morning-plays-regen-btn">
            REGEN
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-sm text-slate-400 text-center py-8 animate-pulse">
          Génération des plays du jour... (peut prendre jusqu'à 30s)
        </div>
      )}

      {data?.error && (
        <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          ⚠️ {data.error}
        </div>
      )}

      {data?.plays && data.plays.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {data.plays.map((p) => (
            <PlayCard key={p.rank} play={p} status={statuses[p.rank]} onAnalyze={onAnalyze} />
          ))}
        </div>
      )}

      {data?.plays && data.plays.length === 0 && !loading && (
        <div className="text-sm text-slate-400 text-center py-6">
          Aucun setup à conviction A/B+ aujourd'hui. C'est NORMAL — un bon trader ne trade pas tous les jours.
        </div>
      )}

      {/* HISTORY + STATS panel */}
      <HistoryPanel owner={owner} />
    </div>
  );
}

function HistoryPanel({ owner }) {
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!owner) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/morning-plays/${owner}/history`, { params: { days: 30 }, timeout: 10000 });
      setHist(r.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [owner]);

  if (!hist) return null;
  const s = hist.stats || {};
  const winRate = s.win_rate_pct || 0;
  const winRateColor = winRate >= 55 ? "text-emerald-400" : winRate >= 45 ? "text-cyan-400" : winRate > 0 ? "text-amber-400" : "text-slate-500";
  const avgPnl = s.avg_daily_pnl_pct || 0;
  const avgPnlColor = avgPnl >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div className="mt-5 pt-4 border-t border-slate-800/60">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white"
          data-testid="history-toggle-btn">
          <BarChart3 className="w-4 h-4 text-cyan-400" />
          Historique &amp; Stats {hist.days_window}j
          <span className="text-cyan-400">{open ? "▲" : "▼"}</span>
        </button>
        {!loading && s.total_days > 0 && (
          <div className="flex gap-3 text-[11px]">
            <span className={`font-bold ${winRateColor}`}>WR {winRate.toFixed(0)}%</span>
            <span className={`font-bold ${avgPnlColor}`}>{avgPnl >= 0 ? "+" : ""}{avgPnl.toFixed(2)}%/j</span>
            <span className="text-slate-500">{s.total_days} jours</span>
          </div>
        )}
      </div>

      {open && (
        <>
          {s.total_days === 0 ? (
            <div className="text-sm text-slate-500 italic text-center py-4">
              Pas encore d'historique. Le premier snapshot sera fait à 22h Paris ce soir.
            </div>
          ) : (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40">
                  <div className="text-[10px] text-slate-500 uppercase">Win rate</div>
                  <div className={`font-mono text-xl font-black ${winRateColor}`}>{winRate.toFixed(0)}%</div>
                  <div className="text-[10px] text-slate-500">{s.wins}W / {s.losses}L</div>
                </div>
                <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40">
                  <div className="text-[10px] text-slate-500 uppercase">P&amp;L moyen / jour</div>
                  <div className={`font-mono text-xl font-black ${avgPnlColor}`}>{avgPnl >= 0 ? "+" : ""}{avgPnl.toFixed(2)}%</div>
                  <div className="text-[10px] text-slate-500">par session</div>
                </div>
                <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40">
                  <div className="text-[10px] text-slate-500 uppercase">Meilleur / pire jour</div>
                  <div className="font-mono text-sm">
                    <span className="text-emerald-400">+{s.best_day_pnl_pct?.toFixed(1) || 0}%</span>
                    <span className="text-slate-600"> / </span>
                    <span className="text-red-400">{s.worst_day_pnl_pct?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="text-[10px] text-slate-500">{s.total_plays} plays totaux</div>
                </div>
                <div className="rounded-lg p-3 bg-slate-800/40 border border-slate-700/40">
                  <div className="text-[10px] text-slate-500 uppercase">Régularité</div>
                  <div className={`font-mono text-xl font-black ${s.consistency_score >= 60 ? "text-emerald-400" : s.consistency_score >= 45 ? "text-cyan-400" : "text-amber-400"}`}>
                    {s.consistency_score?.toFixed(0) || 0}%
                  </div>
                  <div className="text-[10px] text-slate-500">{s.profitable_days}j +</div>
                </div>
              </div>

              {/* Per-day timeline */}
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="history-timeline">
                {hist.history.map((d) => (
                  <div key={d.session_date} className="flex items-center justify-between p-2 rounded bg-slate-800/30 border border-slate-700/30 text-xs">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3 text-slate-500" />
                      <span className="font-mono text-slate-300">{d.session_date}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-400">{d.wins}W</span>
                      <span className="text-red-400">{d.losses}L</span>
                      <span className="text-slate-500">{d.expired}E</span>
                      <span className="text-slate-600">{d.no_entries}NE</span>
                      <span className={`font-mono font-bold ${(d.total_pnl_pct_sum || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {(d.total_pnl_pct_sum || 0) >= 0 ? "+" : ""}{(d.total_pnl_pct_sum || 0).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-slate-600 text-center">
                W=Win L=Loss E=Expired (intraday close) NE=Non-Entered (entry pas atteinte)
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
